<template>
  <a-breadcrumb style="margin: 16px 0">
    <a-breadcrumb-item>贡献者</a-breadcrumb-item>
    <a-breadcrumb-item>领域分析</a-breadcrumb-item>
  </a-breadcrumb>

  <a-radio-group v-model:value="sigOrRepo" style="margin-bottom: 16px;">
    <a-radio-button value="sig">SIG 活跃贡献者</a-radio-button>
    <a-radio-button value="repo">仓库活跃贡献者</a-radio-button>
  </a-radio-group>

  <div style="text-align: left;">
    <span class="caption-and-select-container">
      <span class="caption">时间维度</span>
      <a-select v-model:value="year" class="select">
        <a-select-option v-for="opt in yearOptions" :key="opt.key" :value="opt.value">
          {{ opt.label }}
        </a-select-option>
      </a-select>
    </span>
    <span class="caption-and-select-container">
      <span class="caption">项目维度</span>
      <a-select v-model:value="project" class="select">
        <a-select-option v-for="opt in projectOptions" :key="opt.key" :value="opt.value">
          {{ opt.label }}
        </a-select-option>
      </a-select>
    </span>
    <span class="caption-and-select-container">
      <span class="caption">组织维度</span>
      <a-select v-model:value="employerType" class="select">
        <a-select-option v-for="opt in employerOptions" :key="opt.key" :value="opt.value">
          {{ opt.label }}
        </a-select-option>
      </a-select>
    </span>
  </div>

  <div class="table-container">
    <div style="text-align: left; margin-bottom: 20px;">
      <div class="table-title-row">
        <span class="table-title">{{ sigOrRepo === 'sig' ? 'SIG 贡献统计' : '仓库贡献统计' }}</span>
        <span class="table-subtitle">（按代码量降序排列）</span>
      </div>
      <div v-if="sigOrRepo === 'sig'" class="sig-description-row">
        <span class="sig-description-trigger" @click="sigImageVisible = true">
          仓库流转说明
          <ZoomInOutlined class="sig-description-icon" />
        </span>
      </div>
    </div>
    <a-modal v-model:open="sigImageVisible" :footer="null" :width="860" centered>
      <div class="sig-modal-content">
        <div class="sig-modal-text">
          SIG 下仓库流转流程：创建 SIG 以后可以配套开设孵化仓，孵化运作成熟以后孵化仓可以纳入主干或
          TPC，结束孵化以后原有的孵化仓链接将被移至主仓/TPC。
        </div>
        <img class="sig-modal-image" src="../../assets/sig-contributor-descriptiopn.png" alt="SIG 流转示意图" />
      </div>
    </a-modal>
    <a-spin :spinning="spinning">
      <a-table :columns="columns" :data-source="tableData" :pagination="pagination" :rowKey="row => row.rank"
        :rowClassName="rowClassName">
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'rank'">
            <div class="rank-cell">
              <span class="rank-number">{{ record.rank }}</span>
              <component :is="rankIcon(record.rank)" v-if="rankIcon(record.rank)"
                :style="{ color: rankColor(record.rank) }" class="rank-icon" />
            </div>
          </template>
          <template v-else-if="column.dataIndex === 'name'">
            <div class="name-cell">
              <span :class="{ 'top-name': record.rank <= 3 }">{{ record.name }}</span>
              <div v-if="sigOrRepo === 'sig' && project === null" class="name-tags">
                <a-tag v-if="record.hasOpenHarmonyProject" color="blue">主干</a-tag>
                <a-tag v-if="record.hasOpenHarmonySigProject" color="purple">孵化仓</a-tag>
                <a-tag v-if="record.hasOpenHarmonyTpcProject" color="green">TPC</a-tag>
              </div>
            </div>
          </template>
          <template v-else-if="column.dataIndex === 'contributors'">
            <div class="metric-cell">
              <div class="metric-value">{{ formatNumber(record.contributors) }}</div>
              <div class="bar-bg">
                <div class="bar-fill" :style="{ width: contributorsPercent(record) + '%', backgroundColor: '#4295FF' }">
                </div>
              </div>
            </div>
          </template>
          <template v-else-if="column.dataIndex === 'code'">
            <div class="metric-cell">
              <div class="metric-value">{{ formatNumber(record.code) }}</div>
              <div class="bar-bg">
                <div class="bar-fill" :style="{ width: codePercent(record) + '%', backgroundColor: '#00D676' }"></div>
              </div>
            </div>
          </template>
        </template>
      </a-table>
    </a-spin>

  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import axios from 'axios';
import { notification } from 'ant-design-vue';
import { CrownOutlined, TrophyOutlined, StarOutlined, ZoomInOutlined } from '@ant-design/icons-vue';
const sigOrRepo = ref('sig');

const years = Array.from({ length: 2025 - 2020 + 1 }, (_, i) => 2020 + i);
const yearOptions = [
  { key: 'all', label: '全部', value: null },
  ...years.map((y) => ({ key: y, label: String(y), value: y }))
];
const projectOptions = computed(() => {
  // SIG 模式支持“全部”，仓库模式固定主干/SIG/TPC
  if (sigOrRepo.value === 'sig') {
    return [
      { key: 'all', label: '全部', value: null },
      { key: 'master', label: '主干', value: 'openharmony' },
      { key: 'sig', label: '孵化仓', value: 'openharmony-sig' },
      { key: 'tpc', label: 'TPC', value: 'openharmony-tpc' }
    ];
  } else {
    return [
      { key: 'master', label: '主干', value: 'openharmony' },
      { key: 'sig', label: '孵化仓', value: 'openharmony-sig' },
      { key: 'tpc', label: 'TPC', value: 'openharmony-tpc' }
    ];
  }
});
const employerOptions = [
  { key: 'all', label: '全部', value: null },
  { key: 'huawei', label: '华为', value: 'HUAWEI' },
  { key: 'non_huawei', label: '非华为', value: 'NON_HUAWEI' }
];

const year = ref(null);
const project = ref(null);
const employerType = ref(null);
const spinning = ref(false);
const sigImageVisible = ref(false);
const currentPage = ref(1);
const pagination = computed(() => ({
  pageSize: 20,
  current: currentPage.value,
  onChange: (page) => {
    currentPage.value = page;
  }
}));

// table: columns and data
const columns = computed(() => {
  if (sigOrRepo.value === 'sig') {
    return [
      { title: '排名', dataIndex: 'rank', key: 'rank', width: 80, align: 'center' },
      { title: 'SIG 名称', dataIndex: 'name', key: 'name', width: 300 },
      { title: '贡献者人数', dataIndex: 'contributors', key: 'contributors', width: 200 },
      { title: '代码量（行）', dataIndex: 'code', key: 'code', width: 200 }
    ];
  } else {
    return [
      { title: '排名', dataIndex: 'rank', key: 'rank', width: 80, align: 'center' },
      { title: '仓库名称', dataIndex: 'name', key: 'name', width: 300 },
      { title: '贡献者人数', dataIndex: 'contributors', key: 'contributors', width: 200 },
      { title: '代码量（行）', dataIndex: 'code', key: 'code', width: 200 }
    ];
  }
});

const tableData = ref([]);

const maxContributors = computed(() => {
  // 贡献者人数柱形以全表最大值为基准
  if (!tableData.value.length) return 1;
  return tableData.value.reduce((m, row) => Math.max(m, Number(row.contributors) || 0), 1);
});
const maxCode = computed(() => tableData.value[0]?.code || 1);

const contributorsPercent = (row) => Math.max(0, Math.min(100, (row.contributors / maxContributors.value) * 100));
const codePercent = (row) => Math.max(0, Math.min(100, (row.code / maxCode.value) * 100));

const formatNumber = (n) => {
  if (n === null || n === undefined) return '';
  const num = typeof n === 'number' ? n : Number(n);
  return Number.isNaN(num) ? n : num.toLocaleString();
};

const rowClassName = (_record, index) => (currentPage.value === 1 && index < 3 ? 'top-rank-row' : ''); // 仅第一页前三名高亮

// rank icons and colors for top 3
const rankIcon = (rank) => {
  if (rank === 1) return CrownOutlined;
  if (rank === 2) return TrophyOutlined;
  if (rank === 3) return StarOutlined;
  return null;
};

const rankColor = (rank) => {
  if (rank === 1) return '#f59e0b'; // gold/amber
  if (rank === 2) return '#9ca3af'; // silver/gray
  if (rank === 3) return '#b45309'; // bronze/brown
  return '#6b7280';
};

const mapToRows = (items, isSig) => {
  // SIG/仓库模式字段映射不同，fetchTable 已确保 data 为数组
  return items.map((item, idx) => ({
    rank: idx + 1,
    name: isSig ? item.sigName : item.repo,
    contributors: item.contributorCount || 0,
    code: item.totalChangeNum || 0,
    hasOpenHarmonyProject: isSig ? item.hasOpenHarmonyProject === 1 : false,
    hasOpenHarmonySigProject: isSig ? item.hasOpenHarmonySigProject === 1 : false,
    hasOpenHarmonyTpcProject: isSig ? item.hasOpenHarmonyTpcProject === 1 : false
  }));
};

const fetchTable = async () => {
  const isSig = sigOrRepo.value === 'sig';
  const url = isSig ? '/metric/sigRank' : '/metric/repoRank';
  const params = {
    year: year.value,
    project: project.value,
    employerType: employerType.value
  };

  spinning.value = true;
  try {
    const res = await axios.get(url, { params });
    if (res?.data?.code !== 200 || !Array.isArray(res.data?.data)) {
      throw new Error(res?.data?.msg || '请求失败');
    }
    tableData.value = mapToRows(res.data.data, isSig);
  } catch (error) {
    tableData.value = [];
    notification.error({ message: '数据加载失败', description: error?.message || error });
  } finally {
    spinning.value = false;
  }
};

watch(sigOrRepo, (val) => {
  if (val === 'sig') {
    year.value = null;
    project.value = null;
    employerType.value = null;
  } else {
    year.value = null;
    project.value = 'openharmony';
    employerType.value = null;
  }
});
watch([sigOrRepo, year, project, employerType], fetchTable, { immediate: true });


</script>

<style scoped>
.caption-and-select-container {
  text-align: left;
  margin-right: 24px;
}

.caption {
  margin-right: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #333333;
}

.select {
  width: 120px;
}

.table-container {
  margin-top: 16px;
  padding: 25px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  background-color: #ffffff;
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #111111;
}

.table-title-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.sig-description-row {
  margin-top: 8px;
}

.sig-description-trigger {
  color: #2563eb;
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.sig-description-icon {
  font-size: 14px;
}

.sig-modal-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sig-modal-text {
  padding: 0 16px;
  line-height: 1.6;
  color: #111827;
}

.sig-modal-image {
  width: 100%;
  border-radius: 8px;
  display: block;
}

.table-subtitle {
  margin-left: 8px;
  font-size: 12px;
  color: #6b7280;
}

.metric-cell {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.metric-value {
  font-variant-numeric: tabular-nums;
}

.bar-bg {
  width: 100%;
  height: 6px;
  background-color: transparent;
  border-radius: 4px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 4px;
}

.rank-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.rank-number {
  font-weight: 700;
  font-size: 18px;
}

.rank-icon {
  font-size: 18px;
}

.name-cell {
  display: grid;
  grid-template-columns: 1fr 160px;
  align-items: flex-start;
  column-gap: 12px;
}

.name-tags {
  display: inline-flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 6px;
  text-align: left;
}

.top-name {
  font-weight: 700;
}

:deep(.ant-table-tbody > tr.top-rank-row) {
  background: linear-gradient(90deg, #FDFBE8 0%, #FFFFFF 100%);
}

:deep(.ant-table-tbody > tr.top-rank-row > td) {
  background: transparent;
}
</style>